import React, { useState, useEffect } from 'react';
import { Lock, Info, X, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../api/client';

/**
 * Rollup Breakdown Panel - Shows why a parent's status/dates are what they are
 * Displays:
 * - Status composition (e.g., 5 In Progress, 1 Blocked)
 * - Earliest task start date
 * - Latest task due date
 * - Human-readable explanation
 */
export const RollupBreakdownPanel = ({
  entityType,
  entityId,
  entityName,
  isOpen,
  onClose
}) => {
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && entityType && entityId) {
      loadBreakdown();
    }
  }, [isOpen, entityType, entityId]);

  const loadBreakdown = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getRollupWhy(entityType, entityId);
      setBreakdown(res.data);
    } catch (err) {
      console.error('Failed to load breakdown:', err);
      setError(err.message || 'Failed to load breakdown');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getStatusColor = (status) => {
    const colors = {
      'Blocked': '#8E44AD',
      'Behind': '#E74C3C',
      'Due Date at Risk': '#F1C40F',
      'In Progress': '#3498DB',
      'In Review': '#16A085',
      'Not Started': '#BDC3C7',
      'Completed': '#2ECC71',
    };
    return colors[status] || '#95A5A6';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-[var(--clr-surface-subtle)]">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-[var(--clr-brand-primary)]" />
            <h3 className="text-lg font-semibold text-[var(--clr-text-primary)]">
              Derived Values Breakdown
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--clr-brand-primary)]"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">
              {error}
            </div>
          ) : breakdown ? (
            <div className="space-y-6">
              {/* Entity Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-[var(--clr-text-muted)] uppercase tracking-wide mb-1">
                  {entityType}
                </div>
                <div className="font-medium text-[var(--clr-text-primary)]">
                  {entityName || entityId}
                </div>
              </div>

              {/* Derived Status */}
              <div>
                <h4 className="text-sm font-medium text-[var(--clr-text-secondary)] mb-3 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Derived Status
                </h4>
                <div className="flex items-center gap-3">
                  <span
                    className="px-3 py-1 rounded-full text-white text-sm font-medium"
                    style={{ backgroundColor: getStatusColor(breakdown.derived_status) }}
                  >
                    {breakdown.derived_status}
                  </span>
                  <span className="text-sm text-[var(--clr-text-muted)]">
                    {breakdown.status_explanation}
                  </span>
                </div>
              </div>

              {/* Status Composition */}
              {breakdown.status_composition && Object.keys(breakdown.status_composition).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-[var(--clr-text-secondary)] mb-3">
                    Task Status Breakdown
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(breakdown.status_composition).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: getStatusColor(status) }}
                          />
                          <span className="text-sm">{status}</span>
                        </div>
                        <span className="text-sm font-medium">
                          {count} {count === 1 ? 'task' : 'tasks'}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Progress bar */}
                  <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden flex">
                    {Object.entries(breakdown.status_composition).map(([status, count]) => (
                      <div
                        key={status}
                        className="h-full"
                        style={{ 
                          width: `${(count / breakdown.total_tasks) * 100}%`,
                          backgroundColor: getStatusColor(status)
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Derived Dates */}
              <div>
                <h4 className="text-sm font-medium text-[var(--clr-text-secondary)] mb-3 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Derived Dates
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-[var(--clr-text-muted)] mb-1">
                      Earliest Start (MIN)
                    </div>
                    <div className="font-medium">
                      {breakdown.earliest_start || 'Not set'}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-[var(--clr-text-muted)] mb-1">
                      Latest Due (MAX)
                    </div>
                    <div className="font-medium">
                      {breakdown.latest_due || 'Not set'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4 border-t border-gray-200 pt-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[var(--clr-brand-primary)]">
                    {breakdown.total_tasks}
                  </div>
                  <div className="text-xs text-[var(--clr-text-muted)]">Total Tasks</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {breakdown.completed_tasks}
                  </div>
                  <div className="text-xs text-[var(--clr-text-muted)]">Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[var(--clr-brand-primary)]">
                    {breakdown.percent_complete}%
                  </div>
                  <div className="text-xs text-[var(--clr-text-muted)]">Progress</div>
                </div>
              </div>

              {/* Info Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <strong>Why are these fields locked?</strong>
                  <p className="mt-1">
                    Status and dates for {entityType}s with child items are automatically 
                    derived from their tasks. This ensures data consistency across the hierarchy. 
                    To change these values, update the underlying tasks.
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-opacity-90 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Inline "Derived" indicator with lock icon
 * Shows next to fields that are derived from children
 */
export const DerivedFieldIndicator = ({
  fieldName,
  onClick,
  className = ''
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 text-xs text-[var(--clr-text-muted)] 
        hover:text-[var(--clr-brand-primary)] transition-colors cursor-pointer ${className}`}
      title={`${fieldName} is derived from child tasks. Click to see why.`}
    >
      <Lock className="w-3 h-3" />
      <span>Derived</span>
    </button>
  );
};

/**
 * Collapsible "Why?" section for inline display
 */
export const CollapsibleRollupInfo = ({
  entityType,
  entityId,
  showOnLoad = false
}) => {
  const [isExpanded, setIsExpanded] = useState(showOnLoad);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isExpanded && !breakdown) {
      loadBreakdown();
    }
  }, [isExpanded]);

  const loadBreakdown = async () => {
    try {
      setLoading(true);
      const res = await api.getRollupWhy(entityType, entityId);
      setBreakdown(res.data);
    } catch (err) {
      console.error('Failed to load breakdown:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Blocked': '#8E44AD',
      'Behind': '#E74C3C',
      'In Progress': '#3498DB',
      'Completed': '#2ECC71',
      'Not Started': '#BDC3C7',
    };
    return colors[status] || '#95A5A6';
  };

  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-3 py-2 flex items-center justify-between text-sm text-[var(--clr-text-secondary)] hover:bg-gray-100"
      >
        <span className="flex items-center gap-2">
          <Lock className="w-4 h-4" />
          Why are these values locked?
        </span>
        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {isExpanded && (
        <div className="px-3 py-3 border-t border-gray-200">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[var(--clr-brand-primary)]"></div>
            </div>
          ) : breakdown ? (
            <div className="space-y-3">
              <p className="text-xs text-[var(--clr-text-muted)]">
                {breakdown.status_explanation}
              </p>
              
              {/* Mini status breakdown */}
              <div className="flex flex-wrap gap-2">
                {Object.entries(breakdown.status_composition || {}).map(([status, count]) => (
                  <span 
                    key={status}
                    className="px-2 py-0.5 rounded-full text-xs text-white"
                    style={{ backgroundColor: getStatusColor(status) }}
                  >
                    {status}: {count}
                  </span>
                ))}
              </div>
              
              {/* Dates */}
              <div className="flex gap-4 text-xs text-[var(--clr-text-muted)]">
                {breakdown.earliest_start && (
                  <span>Start: {breakdown.earliest_start}</span>
                )}
                {breakdown.latest_due && (
                  <span>Due: {breakdown.latest_due}</span>
                )}
              </div>
            </div>
          ) : (
            <p className="text-xs text-[var(--clr-text-muted)]">
              Unable to load breakdown
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default RollupBreakdownPanel;
